import { GraphQLSopOutput, GraphQLTestPeriodOutput, GraphQLUserFunctionOutput } from 'src/app/graphql/models/output/treo-reference-data.output.model';
import { Sop } from 'src/app/models/sop';
import { TestPeriod } from 'src/app/models/test-period';
import { UfNode } from 'src/app/models/uf-uc-scn-tree';

/**
 * Converts the raw `sops` GraphQL output into the SOP dropdown options: keeps the latest version
 * (version 0) with numeric names only, dedupes by name, sorts newest-first, and prepends a
 * synthetic "Main" entry mapping to the latest SOP's number. "Main" = the latest SOP. Null entries are dropped. Returns [] when nothing
 * qualifies.
 *
 * @param input GraphQL output of the sops query.
 * @returns SOP view models, "Main" first.
 */
export function convertGraphQLSopsToDto(input: (GraphQLSopOutput | null)[] | null): Sop[] {
	const raw = (input ?? []).filter((s): s is GraphQLSopOutput => !!s);
	// Latest version only, numeric names only, deduped by name (ECU Compare's SopService logic).
	const latest = raw.filter(s => s.version === 0 && !Number.isNaN(Number(s.name)));
	const byName = [...new Map(latest.map(s => [s.name, s])).values()];
	// Newest first.
	const sorted = byName.sort((a, b) => Number(b.name) - Number(a.name));

	const sops: Sop[] = sorted.map(s => ({ id: s.id, name: s.name, sopNumber: Number(s.name) }));
	if (sops.length === 0) {
		return sops;
	}
	// "Main" = the latest SOP (familiar terminology), carrying its numeric value.
	const main: Sop = { id: sorted[0].id, name: 'Main', sopNumber: Number(sorted[0].name) };
	return [main, ...sops];
}

/**
 * Converts the raw `testPeriods` GraphQL output into test-week dropdown options: keeps the latest
 * version (0) with a name, sorts newest-first (by validFrom when available, else by name), and
 * parses the validity window into Dates. Null entries are dropped.
 *
 * @param input GraphQL output of the testPeriods query.
 * @returns Test-period view models, newest first.
 */
export function convertGraphQLTestPeriodsToDto(input: (GraphQLTestPeriodOutput | null)[] | null): TestPeriod[] {
	const raw = (input ?? []).filter((t): t is GraphQLTestPeriodOutput => !!t);
	const latest = raw.filter(t => t.version === 0 && !!t.name);
	// Newest first by validFrom when available, else by name.
	latest.sort((a, b) => {
		const av = a.validFrom ? Date.parse(a.validFrom) : 0;
		const bv = b.validFrom ? Date.parse(b.validFrom) : 0;
		if (av !== bv) return bv - av;
		return b.name.localeCompare(a.name);
	});
	return latest.map(t => ({
		id: t.id,
		name: t.name,
		validFrom: t.validFrom ? new Date(t.validFrom) : undefined,
		validTo: t.validTo ? new Date(t.validTo) : undefined,
	}));
}

/**
 * Converts the nested `configuratorUserFunctions` GraphQL output into the UF → UC → SCN tree for
 * the cascading dropdowns. Entries with a null id are dropped at every level; each node gets a
 * "<id> - <name>" label for display.
 *
 * @param input GraphQL output of the configuratorUserFunctions query.
 * @returns Nested UF view models.
 */
export function convertGraphQLUserFunctionsToDto(input: (GraphQLUserFunctionOutput | null)[] | null): UfNode[] {
	const label = (id: number, name: string) => `${id} - ${name}`.trim();
	return (input ?? [])
		.filter((uf): uf is GraphQLUserFunctionOutput => !!uf && uf.Id != null)
		.map(uf => {
			const ufName = uf.Name ?? '';
			return {
				id: uf.Id as number,
				name: ufName,
				label: label(uf.Id as number, ufName),
				ucs: (uf.UseCases ?? [])
					.filter((uc): uc is NonNullable<typeof uc> => !!uc && uc.Id != null)
					.map(uc => {
						const ucName = uc.Name ?? '';
						return {
							id: uc.Id as number,
							name: ucName,
							label: label(uc.Id as number, ucName),
							scns: (uc.Scenarios ?? [])
								.filter((s): s is NonNullable<typeof s> => !!s && s.Id != null)
								.map(s => {
									const scnName = s.Name ?? '';
									return { id: s.Id as number, name: scnName, label: label(s.Id as number, scnName) };
								}),
						};
					}),
			};
		});
}
